import { ScreenedCareGap, ScreenedQueue } from '../types/timeScreeningStore';
import axios from '../axios';
import { PATIENT_QUERY, V3_TIME_PRESCREENING } from '../constants';
import {
  GetTrialMatchingQueueReqBody,
  RescreenEligibilityReviewRowReq,
  UpdateEligibilityReviewRowReq,
} from '../types/timeScreeningApi';
import {
  ScreenedTrialsForPatientResponseDtoType,
  TimePrescreeningMatchingPatientCountResponseDtoType,
  TimePrescreeningMatchingTabCountsRequestDtoType,
  TimePrescreeningMatchingTabCountsResponseDtoType,
  TimePrescreeningPatientMetadataResponseDtoType,
  TimePrescreeningManualAddRequestDtoType,
  TimePrescreeningScreenedRequestDtoType,
  TimePatientArmEvaluation,
  CreatePatientQueryFeedbackRequestDtoType,
  PatientQueryFeedbackSavedDtoType,
  UpdatePatientQueryFeedbackCommentRequestDtoType,
} from '@tempus/trials-types';
import {
  buildRescreenRequest,
  buildScreeningDecisionRequest,
  unwrapRescreenEvaluationRow,
} from './timePrescreening.patientPage.helpers';
import {
  PatientArmEvaluationsResponse,
  TimePatientQueryAnswersResponseDtoType,
  TimePrescreeningMatchingQueueResponseWithRows,
  TimePrescreeningScreenedPatientsResponseWithRows,
  UnscreenedMatchRowDto,
} from '../types/timePrescreeningApi';
import type { CriteriaGroupsEvaluationsDto } from '../types/trialEvaluation';

export const timeScreeningApi = {
  getMatchingTabCounts: async (
    body: GetTrialMatchingQueueReqBody,
  ): Promise<TimePrescreeningMatchingTabCountsResponseDtoType> => {
    const { trialId, filterBy } = body;
    const payload: TimePrescreeningMatchingTabCountsRequestDtoType = { filterBy };

    const { data } = await axios.post(
      `${V3_TIME_PRESCREENING}/trials/${encodeURIComponent(trialId)}/unscreened-matches/tab-counts`,
      payload,
    );

    return data;
  },
  getMatchingQueueRows: async (
    body: GetTrialMatchingQueueReqBody,
  ): Promise<{ rows: UnscreenedMatchRowDto[]; total: number }> => {
    const { trialId, ...payload } = body;

    const { data } = await axios.post<TimePrescreeningMatchingQueueResponseWithRows>(
      `${V3_TIME_PRESCREENING}/trials/${encodeURIComponent(trialId)}/unscreened-matches`,
      payload,
    );

    return { rows: data.rows, total: data.total };
  },
  getMatchingQueuePatientsCount: async (
    body: GetTrialMatchingQueueReqBody,
  ): Promise<TimePrescreeningMatchingPatientCountResponseDtoType> => {
    const { trialId, states, filterBy } = body;

    const { data } = await axios.post(
      `${V3_TIME_PRESCREENING}/trials/${encodeURIComponent(trialId)}/unscreened-matches/patient-count`,
      { states, filterBy },
    );

    return data;
  },
  getScreenedQueue: async (body: TimePrescreeningScreenedRequestDtoType): Promise<ScreenedQueue> => {
    const { data } = await axios.post<TimePrescreeningScreenedPatientsResponseWithRows>(
      `${V3_TIME_PRESCREENING}/screened/queue`,
      body,
    );

    return {
      count: data.total,
      rows: data.rows,
      loading: false,
    };
  },
  getScreenedCareGaps: async (): Promise<ScreenedCareGap> => {
    // TODO replace with real API call
    return Promise.resolve({ count: 0, loading: false, workflows: [] });
  },
  createManuallyAddedTPAEs: async (
    body: TimePrescreeningManualAddRequestDtoType,
  ): Promise<TimePatientArmEvaluation[]> => {
    return axios.post(`${V3_TIME_PRESCREENING}/manual-add`, body).then(({ data }) => data);
  },
  getPatientTrialEligibilityReviewRows: async (
    timePatientId: string,
    trialId: string,
  ): Promise<PatientArmEvaluationsResponse> => {
    const { data } = await axios.get<PatientArmEvaluationsResponse>(
      `${V3_TIME_PRESCREENING}/trials/${encodeURIComponent(trialId)}/time-patients/${encodeURIComponent(timePatientId)}/arm-evaluations`,
    );

    return data;
  },
  getTrialEvaluation: async (timePatientId: string, trialId: string): Promise<CriteriaGroupsEvaluationsDto> => {
    const { data } = await axios.get<CriteriaGroupsEvaluationsDto>(
      `${V3_TIME_PRESCREENING}/trials/${encodeURIComponent(trialId)}/time-patients/${encodeURIComponent(timePatientId)}/trial-evaluation`,
    );

    return data;
  },
  getPatientTrialMetadataFromPCM: async (timePatientId: string): Promise<ScreenedTrialsForPatientResponseDtoType> => {
    const { data } = await axios.get<ScreenedTrialsForPatientResponseDtoType>(
      `${V3_TIME_PRESCREENING}/patients/${encodeURIComponent(timePatientId)}/trials-metadata`,
    );

    return data;
  },
  getPatientTrialMetadata: async (
    timePatientId: string,
    trialId: string,
  ): Promise<TimePrescreeningPatientMetadataResponseDtoType> => {
    const { data } = await axios.get<TimePrescreeningPatientMetadataResponseDtoType>(
      `${V3_TIME_PRESCREENING}/patients/${encodeURIComponent(timePatientId)}/trials/${encodeURIComponent(trialId)}/patient-metadata`,
    );

    return data;
  },
  getTimeInstitutions: async (): Promise<
    Array<{
      timeLocationId: string;
      timeLocationName: string;
      timeSiteId: string;
      timeSiteName: string;
      type: string;
    }>
  > => {
    const { data } = await axios.get<
      Array<{
        timeLocationId: string;
        timeLocationName: string;
        timeSiteId: string;
        timeSiteName: string;
        type: string;
      }>
    >(`${V3_TIME_PRESCREENING}/time-institutions`);
    return data;
  },
  // APQ answers
  getPatientQueryAnswers: async (
    timePatientId: string,
    trialId: string,
  ): Promise<TimePatientQueryAnswersResponseDtoType> => {
    const { data } = await axios.get<TimePatientQueryAnswersResponseDtoType>(
      `${PATIENT_QUERY}/time/patients/${encodeURIComponent(timePatientId)}/trials/${encodeURIComponent(trialId)}/answers`,
    );

    return data;
  },
  /** Set (`negative`) or undo (`null`) the APQ answer flag; use `updatePatientQueryAnswerFeedbackComment` for comments. */
  recordPatientQueryAnswerFeedback: async (
    timePatientId: string,
    promptId: string,
    body: CreatePatientQueryFeedbackRequestDtoType,
  ): Promise<PatientQueryFeedbackSavedDtoType> => {
    const { data } = await axios.post<PatientQueryFeedbackSavedDtoType>(
      `${PATIENT_QUERY}/feedback/time/patients/${encodeURIComponent(timePatientId)}/prompts/${encodeURIComponent(promptId)}`,
      body,
    );

    return data;
  },
  updatePatientQueryAnswerFeedbackComment: async (
    feedbackId: string,
    body: UpdatePatientQueryFeedbackCommentRequestDtoType,
  ): Promise<PatientQueryFeedbackSavedDtoType> => {
    const { data } = await axios.patch<PatientQueryFeedbackSavedDtoType>(
      `${PATIENT_QUERY}/feedback/time/${encodeURIComponent(feedbackId)}`,
      body,
    );

    return data;
  },
  updateEligibilityReviewRow: async (body: UpdateEligibilityReviewRowReq): Promise<UnscreenedMatchRowDto> => {
    const { evaluationId, payload } = buildScreeningDecisionRequest(body);

    const { data } = await axios.patch<UnscreenedMatchRowDto>(
      `${V3_TIME_PRESCREENING}/evaluations/${encodeURIComponent(evaluationId)}`,
      payload,
    );

    return data;
  },
  rescreen: async (body: RescreenEligibilityReviewRowReq): Promise<UnscreenedMatchRowDto> => {
    const { evaluationId, payload } = buildRescreenRequest(body);

    const { data } = await axios.post<Record<string, unknown>>(
      `${V3_TIME_PRESCREENING}/evaluations/${encodeURIComponent(evaluationId)}/rescreen`,
      payload,
    );

    return unwrapRescreenEvaluationRow(data);
  },
};
